import * as joint from '@joint/core'
import type {
  Model,
  Relationship,
  ElementView,
  Configuration,
} from '../types'
import {
  createPerson,
  createSoftwareSystem,
  createContainer,
  createComponent,
  createCylinder,
  createWebBrowser,
  createMobileDevice,
  createFolder,
  createHexagon,
  createRobot,
  createPipe,
  createEllipse,
  createCircle,
  createDiamond,
} from '../shapes'
import {
  resolveElementStyle,
  determineShape,
  parseTags,
  hasTag,
} from './styleResolver'

/**
 * Element metadata for creating shapes
 */
export interface ElementInfo {
  id: string
  name: string
  description?: string
  technology?: string
  tags?: string
  type: 'Person' | 'SoftwareSystem' | 'Container' | 'Component'
  location?: 'Internal' | 'External' | 'Unspecified'
  parentId?: string
}

/**
 * Build a map of all elements in the model
 */
export function buildElementMap(model: Model): Map<string, ElementInfo> {
  const elements = new Map<string, ElementInfo>()

  // Add people
  model.people?.forEach(person => {
    elements.set(person.id, {
      id: person.id,
      name: person.name,
      description: person.description,
      tags: person.tags,
      type: 'Person',
      location: person.location,
    })
  })

  // Add software systems and their containers/components
  model.softwareSystems?.forEach(system => {
    elements.set(system.id, {
      id: system.id,
      name: system.name,
      description: system.description,
      tags: system.tags,
      type: 'SoftwareSystem',
      location: system.location,
    })

    // Add containers
    system.containers?.forEach(container => {
      elements.set(container.id, {
        id: container.id,
        name: container.name,
        description: container.description,
        technology: container.technology,
        tags: container.tags,
        type: 'Container',
        parentId: system.id,
      })

      // Add components
      container.components?.forEach(component => {
        elements.set(component.id, {
          id: component.id,
          name: component.name,
          description: component.description,
          technology: component.technology,
          tags: component.tags,
          type: 'Component',
          parentId: container.id,
        })
      })
    })
  })

  return elements
}

/**
 * Build a map of all relationships in the model
 */
export function buildRelationshipMap(model: Model): Map<string, Relationship> {
  const relationships = new Map<string, Relationship>()

  // Collect relationships from all elements
  function collectRelationships(rels?: Relationship[]) {
    rels?.forEach(rel => {
      relationships.set(rel.id, rel)
    })
  }

  model.people?.forEach(person => {
    collectRelationships(person.relationships)
  })

  model.softwareSystems?.forEach(system => {
    collectRelationships(system.relationships)
    system.containers?.forEach(container => {
      collectRelationships(container.relationships)
      container.components?.forEach(component => {
        collectRelationships(component.relationships)
      })
    })
  })

  // Also collect from deployment nodes if present
  function collectFromDeploymentNodes(nodes: any[]) {
    nodes?.forEach(node => {
      collectRelationships(node.relationships)
      node.infrastructureNodes?.forEach((infra: any) => {
        collectRelationships(infra.relationships)
      })
      node.softwareSystemInstances?.forEach((instance: any) => {
        collectRelationships(instance.relationships)
      })
      node.containerInstances?.forEach((instance: any) => {
        collectRelationships(instance.relationships)
      })
      if (node.children) {
        collectFromDeploymentNodes(node.children)
      }
    })
  }

  model.deploymentNodes?.forEach(node => {
    collectFromDeploymentNodes([node])
  })

  return relationships
}

/**
 * Create a JointJS shape for an element
 */
export function createShapeForElement(
  elementInfo: ElementInfo,
  elementView: ElementView,
  configuration?: Configuration
): joint.dia.Element {
  const { id, name, description, technology, tags, type, location } = elementInfo
  const { x = 0, y = 0 } = elementView

  // Resolve style based on tags and configuration
  const style = resolveElementStyle(tags, type, configuration, location)
  const shape = determineShape(tags, type, configuration)

  // Create the appropriate shape based on explicit shape setting
  switch (shape) {
    case 'Person':
      return createPerson(id, name, description, x, y, style)

    case 'Cylinder':
      return createCylinder(id, name, technology, description, x, y, style)

    case 'WebBrowser':
      return createWebBrowser(id, name, technology, description, x, y, style)

    case 'MobileDevicePortrait':
      return createMobileDevice(id, name, technology, description, x, y, false, style)

    case 'MobileDeviceLandscape':
      return createMobileDevice(id, name, technology, description, x, y, true, style)

    case 'Folder':
      return createFolder(id, name, technology, description, x, y, style)

    case 'Hexagon':
      return createHexagon(id, name, technology, description, x, y, style)

    case 'Robot':
      return createRobot(id, name, technology, description, x, y, style)

    case 'Pipe':
      return createPipe(id, name, technology, description, x, y, style)

    case 'Ellipse':
      return createEllipse(id, name, technology, description, x, y, style)

    case 'Circle':
      return createCircle(id, name, technology, description, x, y, style)

    case 'Diamond':
      return createDiamond(id, name, technology, description, x, y, style)

    default:
      // Use element type to determine shape
      switch (type) {
        case 'Person':
          return createPerson(id, name, description, x, y, style)
        case 'SoftwareSystem':
          const isExternal = location === 'External' || hasTag(parseTags(tags), 'External')
          return createSoftwareSystem(id, name, description, x, y, isExternal, style)
        case 'Container':
          // Check for database
          if (hasTag(parseTags(tags), 'Database')) {
            return createCylinder(id, name, technology, description, x, y, style)
          }
          return createContainer(id, name, technology, description, x, y, style)
        case 'Component':
          return createComponent(id, name, technology, description, x, y, style)
        default:
          // Default to software system shape
          return createSoftwareSystem(id, name, description, x, y, false, style)
      }
  }
}

/**
 * Default positions for automatic layout when positions are 0,0
 */
export function applyAutomaticLayout(
  elements: joint.dia.Element[],
  viewWidth: number = 1200,
  viewHeight: number = 800
): void {
  // Simple grid layout for elements without positions
  const gridCols = Math.ceil(Math.sqrt(elements.length))
  const cellWidth = viewWidth / (gridCols + 1)
  const cellHeight = viewHeight / (Math.ceil(elements.length / gridCols) + 1)

  let col = 0
  let row = 0

  elements.forEach(element => {
    const pos = element.position()
    // Only apply auto-layout if position is 0,0 (unset)
    if (pos.x === 0 && pos.y === 0) {
      element.position(
        cellWidth * (col + 1) - 100,
        cellHeight * (row + 1) - 80
      )
      col++
      if (col >= gridCols) {
        col = 0
        row++
      }
    }
  })
}
